// js/shared/campaigns.js — WHERE A LEAD CAME FROM, decided in one place.
//
// QUEUE ITEM IE. The printed business card's QR code goes to `kinetiqaf.com/c`,
// which forwards to `/card?utm_source=card&utm_medium=qr`. The point of the
// parameter is that Reports › Lead funnel can answer "did the cards work?"
// without the trainer having to ask every enquirer how they found him.
//
// ── A CAMPAIGN CODE IS AN ALLOWLIST, NOT A STRING ──────────────────────────
//
// `utm_source` arrives from the URL, which means it arrives from the visitor.
// A reader that turned whatever it found into a row label would let anybody
// write a line into the trainer's report — not a security hole (the value is
// escaped where it renders), but noise in the one number this feature exists
// to produce, and noise in a report is indistinguishable from a wrong report.
//
// So `CAMPAIGNS` is the whole vocabulary: a code the browser may send and the
// words the report shows for it. Anything else normalizes to null and the lead
// keeps the source it would have had anyway. This is the same shape the
// settings registry uses for an `enum` — "a value the form cannot produce is
// also a value the reader will not honour" — pointed at a query parameter.
//
// ── WHY THIS IS A FILE AND js/shared/utm.js IS ANOTHER ─────────────────────
//
// This half is PURE: an object and three functions, no DOM, no storage, no
// window. js/admin/reports.js imports it, and `js/admin/reports.js` is an EAGER
// module script in admin.html — so everything in here is in /admin's first
// paint whether the trainer ever opens Reports or not (CI-META-1y counts it).
// The BROWSER half — remembering the campaign in `sessionStorage` for the
// visit, and the `window` bridge the two public forms read — is only ever run
// by a public page, and putting it here would bill /admin for code it cannot
// execute. Measured: 0.5 KB gz of /admin's eager payload, for a feature whose
// reader is one report.
//
// So the split is not tidiness, it is the seam. Same reason
// scripts/design/click-sweep-model.mjs is separate from its driver.

/**
 * Every campaign the product knows, code → the words Reports shows.
 *
 * `card` is the printed business card (queue item IE). A new campaign is a new
 * row here and nothing else: the forms already send whatever code they saw, and
 * the funnel already reads this map.
 */
export const CAMPAIGNS = Object.freeze({
  card: 'Card',
});

/**
 * The campaign code for this visit, or null.
 *
 * `search` is the current `location.search`; `stored` is whatever an earlier
 * page of the same visit remembered. The URL wins when it names a campaign this
 * product knows, then the remembered one, then nothing — the same three-rung
 * shape `marketingLangFrom` uses, so a reader only has to learn it once.
 */
export function campaignFrom({ search, stored } = {}) {
  let param = null;
  try { param = new URLSearchParams(search || '').get('utm_source'); }
  catch (_) { param = null; /* a malformed query string is not a campaign */ }
  for (const candidate of [param, stored]) {
    const code = String(candidate == null ? '' : candidate).trim().toLowerCase();
    if (code && Object.prototype.hasOwnProperty.call(CAMPAIGNS, code)) return code;
  }
  return null;
}

/** The words Reports shows for a code, or null for anything it does not know. */
export function campaignLabel(code) {
  const key = String(code == null ? '' : code).trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(CAMPAIGNS, key) ? CAMPAIGNS[key] : null;
}

/**
 * What "Where leads come from" shows for one lead or intake.
 *
 * The campaign wins when there is one, because it is the more specific answer:
 * a lead that arrived through the card is still a web contact, and "Card" is
 * the fact the trainer is trying to learn. With no campaign the row keeps the
 * source it already had, so every pre-IE lead reads exactly as it did before.
 */
export function leadSourceLabel({ utm_source: utmSource, source, fallback } = {}) {
  return campaignLabel(utmSource) || source || fallback || 'web inquiry';
}
